<template>
  <main class="home" v-if="!loading">
    <div class="text-right">
      <div v-on:click="dismiss" class="upper-close px-3 pt-1">
        <i class="fa-solid fa-x"></i>
      </div>
    </div>
    <IdentityVerification
      v-if="showKyc"
      @next="onNationalIdValidationSuccess"
      @on-validation-failed="onNationalIdValidationFailed"
      :require-validation-response="requiresIdentityResponse"
      :hide-close-button="true"
    />
    <FullScreenModal
      :isOpen="isModalOpen"
      @onClose="closeResult"
      class="pt-5 pb-1"
    >
      <VerificationResult
        :icon="resultIcon"
        :title="resultTitle"
        :show-device-tag="resultError"
        :description="resultDescription"
        :btnText="resultBtnText"
        :error="resultError"
        @dismissFunc="resultDismissFunc"
        :custom-footer="resultError"
      >
        <div class="w-100 mt-auto mb-0">
          <Button
            id="wp-btn"
            class="text-center p-3 mb-3"
            @click="openWhatsApp()"
          >
            <p class="text-center text-white fw-bold my-auto">
              {{ t("PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.WHATSAPP") }}
            </p>
          </Button>
          <Button class="text-center p-3" @click="resultDismissFunc()">
            <p class="text-center text-white fw-bold my-auto">
              {{ resultBtnText }}
            </p>
          </Button>
        </div>
      </VerificationResult>
    </FullScreenModal>
    <div v-if="!showKyc">
      <div class="card-container d-flex flex-column align-items-center mt-3">
        <img
          :src="showPinCard"
          alt="cardPin"
          class="card-pin d-flex justify-content-center align-items-center"
        />
        <h6 class="mt-4">{{ t("PAYJOY_CARD_VIEWS.SHOW_PIN") }}</h6>
        <iframe
          class="embed-responsive-item w-100 mt-3"
          id="iframe"
          :src="IFrameUrl"
        ></iframe>
        <p class="text-center p-3 change-pin mt-3" @click="showPinChange">
          {{ t("PAYJOY_CARD_VIEWS.CHANGE_PIN") }}
        </p>
      </div>
    </div>
  </main>
  <LoadingOverlay :loading="loading" />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useRouter } from "vue-router";
import FullScreenModal from "@/apps/credit-line/components/FullScreenModal.vue";
import VerificationResult from "@/apps/credit-line/components/Result.vue";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import Button from "@/apps/credit-line/components/CreditPull/Button.vue";
import {
  AvailableFeatures,
  Country,
  FeatureControlService,
} from "@/apps/credit-line/services/feature-control.service";
import { useRoute } from "vue-router";
import { areValidationRequired } from "../../services/kyc";
import { KycUserFlow } from "../../enums/kycEnums";
import { IBaseResponse } from "../../models/IBaseResponse";
import { AxiosError } from "axios";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CreditCardShowPin",
  emits: ["showPinChange"],
  components: {
    IdentityVerification,
    LoadingOverlay,
    FullScreenModal,
    VerificationResult,
    Button,
  },
  props: {
    biometricVerified: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const CreditLineService = new Service();
    const { t } = useI18n();
    const IFrameUrl = ref<string>("");
    const requiresIdentityResponse = ref();
    const showKyc = ref(false);
    const loading = ref<boolean>(true);
    const validationFailed = ref<boolean>(false);
    const router = useRouter();
    const { fetchSummary, lastFourDigits } = useCreditLineSummary();
    const isModalOpen = ref<boolean>(false);
    const resultIcon = ref<string>();
    const resultTitle = ref<string>();
    const resultDescription = ref<string>();
    const resultBtnText = ref<string>();
    const resultError = ref<boolean>(false);
    const showfullUserDataFrom = ref<boolean>(false);
    const setLoading = (isLoading: boolean) => (loading.value = isLoading);
    const key = ref<number>(0);
    const showPinCard = useAsset("img/credit-line/credit-card/showPinCard.png");
    const route = useRoute();
    const lastFour = ref<string>("");

    const openWhatsApp = () => {
      const helpMsg = t("CL_WP_HELP_MSG", {
        deviceTag: AuthenticationHelper.getDeviceTag(),
      });
      window.open("https://wa.me/+525575028669?text=" + helpMsg, "_system");
    };

    const showPinChange = () => {
      emit("showPinChange");
    };

    const fetchIsNationalIdValidationRequired = async (
      creditLineId: string
    ) => {
      try {
        const response = await areValidationRequired({
          creditLineId: Number(creditLineId),
          userFlow: KycUserFlow.creditCardShowPin,
          deviceTag: AuthenticationHelper.getDeviceTag(),
        });

        if (response.data.wasSuccessful) {
          const areValidationsRequired =
            response.data.requiredValidations.length > 0;
          showKyc.value = areValidationsRequired && !props.biometricVerified;
          if (areValidationsRequired) {
            requiresIdentityResponse.value = response.data;
          } else {
            await onNationalIdValidationSuccess();
          }
          return;
        }

        onNationalIdValidationFailed();
      } catch (error) {
        onNationalIdValidationFailed();
        ErrorHandler.logCreditlineErrorResponse(error as IBaseResponse);
      }
    };

    const getInitialData = async () => {
      setLoading(true);
      try {
        return await fetchSummary();
      } catch (error) {
        // Log error for metrics
        ErrorHandler.handleNetworkErrorAndLog(
          error as AxiosError,
          () => null,
          "getSummary::CreditCardShowPin"
        );
        // Navigate to error display
        router.push({
          name: "CreditLineApiErrorDisplay",
        });
      }
    };

    const onNationalIdValidationSuccess = async () => {
      showKyc.value = false;
    };

    const onNationalIdValidationFailed = () => {
      if (isAndroid()) Android.finishedLoading();
      setLoading(false);
      validationFailed.value = true;
      resultIcon.value = useAsset("img/credit-line/error_warning_icon.svg");
      resultTitle.value = t("PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_TITLE");
      resultDescription.value = t(
        "PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_DESCRIPTION"
      );
      resultBtnText.value = t("PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_BTN");
      resultError.value = true;
      isModalOpen.value = true;
    };

    const dismiss = () => {
      router.replace({
        name: "CreditCardView",
      });
    };

    return {
      t,
      IFrameUrl,
      showKyc,
      requiresIdentityResponse,
      loading,
      isModalOpen,
      resultIcon,
      resultTitle,
      resultDescription,
      resultBtnText,
      resultError,
      showfullUserDataFrom,
      openWhatsApp,
      validationFailed,
      router,
      route,
      fetchSummary,
      lastFourDigits,
      showPinChange,
      lastFour,
      setLoading,
      onNationalIdValidationSuccess,
      fetchIsNationalIdValidationRequired,
      key,
      showPinCard,
      dismiss,
      getInitialData,
      onNationalIdValidationFailed,
      CreditLineService,
    };
  },
  async mounted() {
    try {
      const FeatureControl = new FeatureControlService();
      const summary = await this.getInitialData();
      if (summary?.creditLineId) {
        this.lastFour = (
          this.route.query?.lastFour == null
            ? this.lastFourDigits
            : this.route.query?.lastFour
        ) as string;
        const kycOn = FeatureControl.isFeatureEnabled(
          AvailableFeatures.KYC,
          summary.country as Country
        );
        if (kycOn) {
          await this.fetchIsNationalIdValidationRequired(
            `${summary.creditLineId}`
          );
        } else {
          await this.onNationalIdValidationSuccess();
        }
      }
      if (isAndroid()) {
        Android.finishedLoading();
      }

      try {
        const res = await this.CreditLineService.getCreditCardPinNumber({
          deviceTag: AuthenticationHelper.getDeviceTag(),
          lastFour: this.lastFour,
        });
        this.IFrameUrl = encodeURI(res.data.iframeUrl || "");
      } catch (err) {
        console.error(err);
      }
    } catch (error) {
      ErrorHandler.logCreditlineErrorResponse(error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    closeResult() {
      Android.dismissWebView();
    },
    resultDismissFunc() {
      this.router.replace({ name: "CreditLineHelpView" });
      return;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
.home {
  overflow-y: auto;
}

.change-pin {
  font-size: 16px;
  color: #007c3b;
  cursor: pointer;
  font-weight: bold;
}

.close-icon {
  width: 40px;
  height: 40px;
}

.card-pin {
  width: 229px;
  height: 145px;
}

.card-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
